// ErrorBoundary — catches render-time exceptions in any wrapped subtree so a
// single broken component no longer turns the whole screen black (shadcn
// Dialog's backdrop covers everything when its content throws).
//
// Usage:
//   <ErrorBoundary label="PaymentFlow" onReset={() => window.location.reload()}>
//     <PaymentModal ... />
//     <OrderCompleteDialog ... />
//   </ErrorBoundary>
//
// Displays a bilingual fallback UI with the error message + "Reload" and
// "Dismiss" buttons so the cashier can recover without rebooting.

import React from "react";
import { tr } from '@/i18n/tr';
import { reportClientError, reloadOnceForStaleBuild } from '@/utils/errorReporter';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Surface to the browser console for debugging on the cashier's machine.
    // Also attempts to surface to the in-app toast stack when available.
    console.error(`[ErrorBoundary:${this.props.label || "unknown"}]`, error, info);
    try {
      reportClientError({
        message: error?.message || String(error),
        stack: error?.stack,
        componentStack: info?.componentStack,
        label: this.props.label,
        source: "error-boundary",
      });
    } catch (_e) {}
    // Stale-build errors (old cached JS after a deploy) — auto-reload once to self-heal.
    if (reloadOnceForStaleBuild(error?.message || error)) return;
    try {
      // Dynamically require sonner so SSR / tests don't explode.
      // eslint-disable-next-line global-require
      const { toast } = require("sonner");
      toast.error(`${tr('x_b491196a')} ${String(error?.message || error).slice(0, 120)}`, { duration: 8000 });
    } catch (_e) {
      // ignore
    }
  }

  reset = () => {
    this.setState({ error: null, info: null });
    if (typeof this.props.onReset === "function") {
      try { this.props.onReset(); } catch (_e) {}
    }
  };

  reload = () => {
    try { window.location.reload(); } catch (_e) {}
  };

  render() {
    if (!this.state.error) return this.props.children;
    const raw = String(this.state.error?.message || this.state.error || "Unknown error");
    // 🗣️ Translate common technical errors into words a cashier understands
    const friendly = (() => {
      if (/Loading chunk|ChunkLoadError|Loading CSS chunk/i.test(raw)) return "Se publicó una versión nueva de Sellox; pulse Recargar. / 系统刚发布了新版本，请点「刷新」。";
      if (/Select\.Item.*value prop/i.test(raw)) return "Un menú recibió un dato vacío y la pantalla no pudo dibujarse.";
      if (/network|fetch|timeout/i.test(raw)) return "Problema de red al cargar la pantalla.";
      if (/undefined|null|of null|of undefined|is not a function/i.test(raw)) return "Un dato incompleto impidió dibujar la pantalla.";
      if (/out of memory|Maximum call/i.test(raw)) return "La pantalla se sobrecargó.";
      return "La pantalla tuvo un problema (sus datos están guardados).";
    })();
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          background: "rgba(15, 23, 42, 0.95)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            background: "#1e293b",
            border: "1px solid #dc2626",
            borderRadius: 12,
            padding: 28,
            color: "#fff",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
            {tr('x_a415f6c2')}
          </h2>
          <p style={{ fontSize: 13, color: "#cbd5e1", marginBottom: 14, lineHeight: 1.5 }}>
            {tr('x_d470d3d3')}
            <br />
            <span style={{ color: "#94a3b8", fontSize: 12 }}>
              La pantalla falló — verifique en Últimas ventas si la orden existe antes de entregar.
            </span>
          </p>
          <p
            style={{
              background: "#0f172a",
              border: "1px solid #334155",
              borderRadius: 6,
              padding: 10,
              fontSize: 13,
              color: "#fbbf24",
              lineHeight: 1.5,
              marginBottom: 10,
            }}
            data-testid="error-boundary-friendly"
          >
            💡 {friendly}
          </p>
          <details style={{ marginBottom: 16 }}>
            <summary style={{ fontSize: 11, color: "#64748b", cursor: "pointer" }}>Detalle técnico</summary>
            <pre
              style={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 6,
                padding: 8,
                fontSize: 10,
                color: "#fca5a5",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 100,
                overflow: "auto",
                marginTop: 6,
              }}
            >
              {raw}
            </pre>
          </details>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={this.reset}
              data-testid="error-boundary-dismiss"
              style={{
                flex: 1,
                padding: "10px 16px",
                background: "#10b981",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {tr('x_8d2f4bdd')}
            </button>
            <button
              onClick={this.reload}
              data-testid="error-boundary-reload"
              style={{
                flex: 1,
                padding: "10px 16px",
                background: "#3b82f6",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {tr('x_5428abef')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
